import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { CAAction, CustomerFiles, Customers } from '../actions/index';
import * as uploadActions from '../file-upload/file-upload.actions';
import * as selectors from '../reducers/index';
import { getSelectedId } from '../reducers/index';
import { CustomersService } from '@careapp-group/ng-sdk';

@Injectable()
export class CustomerFilesEffects {
  autoAssignFileToCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadActions.ResponseFileUploadCompleteAction),
      filter((action) => action.uploadType === 'customer'),
      withLatestFrom(this.store.select(selectors.getSelectedId('customers'))),
      switchMap(([action, id]) =>
        this.customersService.addFile({ id, fileRecordRequestDto: action.response }).pipe(
          map((res) => Customers.ResponseSuccessAddCustomerFile(res)),
          catchError((err) => of(Customers.ResponseFailAddCustomerFile(err)))
        )
      )
    )
  );

  assignCustomerFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Customers.RequestAssignCustomerFile),
      switchMap((action) =>
        this.customersService
          .addFile({
            id: action.customerId,
            fileRecordRequestDto: {
              id: action.fileId,
              roles: action.roles,
            },
          })
          .pipe(
            map((file) =>
              Customers.ResponseSuccessAssignCustomerFile({
                customerId: action.customerId,
                file,
              })
            ),
            catchError((error) =>
              of(
                Customers.ResponseFailAssignCustomerFile({
                  customerId: action.customerId,
                  fileId: action.fileId,
                  error,
                })
              )
            )
          )
      )
    )
  );

  removeFileFromCustomer = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerFiles.REQUEST_DESTROY_BY_ID),
      withLatestFrom(this.store.select(selectors.getSelectedId('customers'))),
      switchMap(([action, id]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Remove File?',
              body: 'Are you sure you want to remove this file? This can not be undone',
              confirm: 'Delete',
              cancel: 'Cancel',
              confirmColor: 'warn',
            },
          })
          .afterClosed()
          .pipe(map((result) => [action, id, result]))
      ),
      switchMap(([action, id, result]) =>
        result
          ? this.customersService.deleteFile({ id, fileId: action.payload.id }).pipe(
              map((_) => CustomerFiles.ResponseSuccessDestroyByIdAction(action.payload)),
              catchError((err) => of(CustomerFiles.ResponseFailDestroyByIdAction(err)))
            )
          : of(CustomerFiles.ResponseFailDestroyByIdAction())
      )
    )
  );

  updateCustomerFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerFiles.REQUEST_SAVE),

      withLatestFrom(this.store.select(getSelectedId('customers'))),
      mergeMap(([action, id]) =>
        this.customersService
          .updateFile({
            id,
            fileId: action.payload.id,
            updateFileRecordRequestDto: { ...action.payload.data },
          })
          .pipe(
            map((result) => CustomerFiles.ResponseSuccessSaveAction(result)),
            catchError((error) => of(CustomerFiles.ResponseFailSaveAction(error)))
          )
      )
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private customersService: CustomersService,
    private dialog: MatDialog
  ) {}
}
